import React, { useState } from "react";
import { ButtonAdd } from "@/components/ui/button";
import { formatPrice } from "@/lib/utils";
import { useModal, useRestaurantCurrency } from "@/lib/hooks";
import { useGetMenuCombosQuery } from "@/redux/services/menu/menu_combo.api";
import { MenuCombo } from "@/redux/services/menu/menu_combo.type";
import MenuComboFormDrawer from "./MenuComboFormDrawer";
import MenuComboDetailsDrawer from "./MenuComboDetailsDrawer";

interface MenuComboListProps {
  menuId: string;
  isReadOnly?: boolean;
}

const MenuComboList: React.FC<MenuComboListProps> = ({ menuId, isReadOnly }) => {
  const currency = useRestaurantCurrency();
  const { data: combos, isLoading, refetch } = useGetMenuCombosQuery({ menuId });

  const {
    isOpen: isFormOpen,
    item: editTarget,
    openModal: addCombo,
    openEditModal: openEdit,
    closeModal: closeForm,
  } = useModal<MenuCombo>();

  const [detailsId, setDetailsId] = useState<string | null>(null);

  const handleEditFromDetails = (combo: MenuCombo) => {
    setDetailsId(null);
    openEdit(combo);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse p-4">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="flex items-center gap-4">
          <div className="h-24 bg-gray-200 rounded w-1/4"></div>
          <div className="h-24 bg-gray-200 rounded w-1/4"></div>
          <div className="h-24 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  const hasCombos = !!combos && combos.results.length > 0;

  return (
    <div className="border-b pb-4 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Combos</h3>
        {!isReadOnly && (
          <ButtonAdd label="Ajouter un combo" onClick={addCombo} />
        )}
      </div>

      {hasCombos ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {combos!.results.map((combo) => (
            <ComboCard
              key={combo.id}
              combo={combo}
              currency={currency}
              onView={() => setDetailsId(combo.id)}
            />
          ))}
        </div>
      ) : (
        <p className="text-gray-500">
          {isReadOnly
            ? "Aucun combo pour ce menu."
            : "Aucun combo n'a encore été créé. Ajoutez-en un pour commencer."}
        </p>
      )}

      <MenuComboDetailsDrawer
        comboId={detailsId}
        isOpen={!!detailsId}
        onClose={() => setDetailsId(null)}
        onEdit={handleEditFromDetails}
      />

      <MenuComboFormDrawer
        isOpen={isFormOpen}
        onClose={closeForm}
        combo={editTarget}
        menuId={menuId}
        onSuccess={refetch}
      />
    </div>
  );
};

interface ComboCardProps {
  combo: MenuCombo;
  currency: string;
  onView: () => void;
}

const ComboCard: React.FC<ComboCardProps> = ({ combo, currency, onView }) => {
  return (
    <button
      type="button"
      onClick={onView}
      className={`text-left border rounded-lg p-4 bg-white transition-colors hover:border-primary hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
        !combo.isAvailable ? "opacity-75" : ""
      }`}
    >
      <div className="flex justify-between items-start">
        <h4 className="font-medium text-base">
          {combo.name}
        </h4>
        <div className="flex flex-col items-end">
          <span className="text-primary font-medium">
            {formatPrice(combo.price / 100, currency)}
          </span>
          {!combo.isAvailable && (
            <span className="text-xs text-white bg-red-600 px-2 py-1 rounded mt-1">
              Indisponible
            </span>
          )}
        </div>
      </div>

      {combo.description && (
        <p className="text-gray-600 text-sm mt-1">{combo.description}</p>
      )}

      <div className="mt-2">
        <span className="text-sm text-gray-500">
          {combo.components.length} composants
        </span>
      </div>
    </button>
  );
};

export default MenuComboList;
